// SPDX-License-Identifier: AGPL-3.0-or-later

import { useState, type FormEvent } from 'react';
import { Button, Card, Input, Label, Stack } from '@intakeform/ui';
import {
  useCreateWebhook,
  useDeleteWebhook,
  useRotateWebhookSecret,
  useTestWebhook,
  useUpdateWebhook,
  useWebhooks,
  type WebhookWithSecret,
} from '../../api/webhooks.js';

/**
 * Admin-only panel for completion webhooks (4d5). On form completion the
 * platform POSTs a thin, HMAC-signed `response.completed` event to each
 * enabled endpoint whose form-type filter matches. The signing secret is
 * shown once at create/rotate; consumers verify `X-Intakeform-Signature`.
 */
export function WebhooksPanel() {
  const { data: hooks, error: loadError } = useWebhooks();
  const create = useCreateWebhook();
  const update = useUpdateWebhook();
  const rotate = useRotateWebhookSecret();
  const test = useTestWebhook();
  const remove = useDeleteWebhook();

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [formType, setFormType] = useState('');
  const [revealed, setRevealed] = useState<{ name: string; secret: string } | null>(null);

  const onCreate = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (name.trim().length === 0 || url.trim().length === 0) return;
    create.mutate(
      {
        name: name.trim(),
        url: url.trim(),
        formTypeFilter: formType.trim().length > 0 ? formType.trim() : null,
      },
      {
        onSuccess: (w: WebhookWithSecret) => {
          setRevealed({ name: w.name, secret: w.secret });
          setName('');
          setUrl('');
          setFormType('');
        },
      },
    );
  };

  const list = hooks ?? [];

  return (
    <Card heading="Webhooks" variant="default" data-webhooks-panel>
      <p className="text-sm text-muted-foreground" style={{ marginBottom: 12 }}>
        POST a signed <code>response.completed</code> event to your service when a form is
        submitted. The payload carries IDs + status only (no answers). Verify the{' '}
        <code>X-Intakeform-Signature</code> header (<code>t=…,v1=HMAC-SHA256(t.body)</code>) with
        the signing secret shown once below.
      </p>

      {loadError !== null && loadError !== undefined ? (
        <div className="error-banner text-sm" role="alert" style={{ marginBottom: 12 }}>
          {loadError.message}
        </div>
      ) : null}

      {revealed !== null ? (
        <div
          role="status"
          aria-live="polite"
          className="text-sm"
          style={{
            marginBottom: 16,
            padding: 12,
            border: '1px solid rgb(var(--color-border))',
            borderRadius: 'var(--radius-md)',
            background: 'rgb(var(--color-surface))',
          }}
          data-webhook-secret
        >
          <strong>Signing secret for “{revealed.name}”.</strong> Copy it now — it won’t be shown
          again:
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <code style={{ wordBreak: 'break-all', flex: 1 }}>{revealed.secret}</code>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                void navigator.clipboard?.writeText(revealed.secret);
              }}
            >
              Copy
            </Button>
            <Button type="button" variant="ghost" onClick={() => setRevealed(null)}>
              Dismiss
            </Button>
          </div>
        </div>
      ) : null}

      <form onSubmit={onCreate} style={{ maxWidth: 560 }}>
        <Stack gap={16}>
          <Stack gap={4}>
            <Label htmlFor="webhook-name" required>
              Name
            </Label>
            <Input
              id="webhook-name"
              type="text"
              value={name}
              data-webhook-name
              onChange={(e) => setName(e.currentTarget.value)}
              placeholder="Booking service"
            />
          </Stack>
          <Stack gap={4}>
            <Label htmlFor="webhook-url" required>
              Endpoint URL
            </Label>
            <Input
              id="webhook-url"
              type="url"
              value={url}
              data-webhook-url
              onChange={(e) => setUrl(e.currentTarget.value)}
              placeholder="https://hooks.your-service.example.com/intakeform"
            />
          </Stack>
          <Stack gap={4}>
            <Label htmlFor="webhook-form-type">Only for form type (optional)</Label>
            <Input
              id="webhook-form-type"
              type="text"
              list="webhook-form-type-options"
              value={formType}
              data-webhook-form-type
              onChange={(e) => setFormType(e.currentTarget.value)}
              placeholder="all forms"
            />
            <datalist id="webhook-form-type-options">
              <option value="intake" />
              <option value="booking" />
              <option value="follow_up" />
            </datalist>
          </Stack>

          {create.error !== null && create.error !== undefined ? (
            <div className="error-banner text-sm" role="alert" aria-live="polite">
              {create.error.message}
            </div>
          ) : null}

          <div>
            <Button
              type="submit"
              variant="primary"
              disabled={create.isPending || name.trim().length === 0 || url.trim().length === 0}
            >
              {create.isPending ? 'Creating…' : 'Add webhook'}
            </Button>
          </div>
        </Stack>
      </form>

      <div style={{ marginTop: 20 }}>
        {list.length === 0 ? (
          <p className="text-sm text-muted-foreground">No webhooks configured.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} data-webhooks-list>
            {list.map((w) => (
              <li
                key={w.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '10px 0',
                  borderBottom: '1px solid rgb(var(--color-border))',
                }}
              >
                <span className="text-sm" style={{ minWidth: 0 }}>
                  <strong>{w.name}</strong>{' '}
                  <span className="text-muted-foreground">· {w.formTypeFilter ?? 'all forms'}</span>
                  <br />
                  <code style={{ wordBreak: 'break-all' }}>{w.url}</code>{' '}
                  {!w.enabled ? <span className="text-muted-foreground">(disabled)</span> : null}
                </span>
                <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={update.isPending}
                    onClick={() => update.mutate({ id: w.id, patch: { enabled: !w.enabled } })}
                  >
                    {w.enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={test.isPending}
                    onClick={() => test.mutate(w.id)}
                  >
                    Test
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={rotate.isPending}
                    onClick={() =>
                      rotate.mutate(w.id, {
                        onSuccess: (rotated) =>
                          setRevealed({ name: rotated.name, secret: rotated.secret }),
                      })
                    }
                  >
                    Rotate
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(w.id)}
                  >
                    Delete
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
